import { useState } from 'react';
import { apiV2 } from '../lib/apiV2';
import { isSignedIn, getSession } from '../lib/auth';
import { AuthSheet } from './AuthSheet';

/**
 * The phone half of device-code pairing (?link= / tab=link).
 *
 * The headset shows a 6-character code; this screen binds it to the signed-in
 * account. It deliberately names the account being signed into BEFORE the
 * confirm: device-code flows are phishable by construction — someone can read
 * a code aloud and ask you to type it — and the only real defence is that the
 * person typing can see exactly what they are authorising.
 */
export function LinkDevice() {
  const [signedIn] = useState(isSignedIn());
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!signedIn) {
    return <AuthSheet title="Sign in to connect your headset" />;
  }

  const submit = async () => {
    const c = code.trim().toUpperCase().replace(/[^0-9A-Z]/g, '');
    if (c.length < 4) {
      setError('Type the code shown in your headset.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await apiV2.claimDeviceCode(c);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not connect that headset.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card marquee bulbs">
        <h2 className="display">Headset connected</h2>
        <p className="soft">
          Your headset is signed in as <strong>{getSession()?.email ?? 'you'}</strong>. It should drop
          you at your nights within a couple of seconds — you can take this phone away now.
        </p>
      </div>
    );
  }

  return (
    <div className="card marquee">
      <h2 className="display">Connect your headset</h2>
      <p className="soft">
        Signing in as <strong>{getSession()?.email ?? 'your account'}</strong>. Type the 6-character
        code showing in the headset.
      </p>

      <label>Code from the headset</label>
      <input
        type="text"
        className="code-input"
        value={code}
        maxLength={8}
        inputMode="text"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        placeholder="ABC123"
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        autoFocus
      />

      {error && <div className="notice error">{error}</div>}

      <div className="form-actions">
        <button className="btn primary" onClick={submit} disabled={busy}>
          {busy ? <span className="spinner" /> : 'Connect headset'}
        </button>
      </div>
      <p className="hint-line">
        Codes last 10 minutes and work once. If it’s expired, start again in the headset.
      </p>
    </div>
  );
}
